
<script setup>
import { toRefs } from 'vue'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { useGlobalStore } from '@/stores/GlobalStore'

const emit = defineEmits(['closeDialog', 'submitDialog'])
const props = defineProps({
  customHeader: { type: Boolean, default: false },
  openDialogField: { type: String, default: 'openDialog' },
  warningBlock: { type: Boolean, default: false },
  customSubmitButtonText: { type: String, default: 'Save Changes' },
  customCancelButtonText: { type: String, default: 'Cancel' },
  cancelButtonNeeded: { type: Boolean, default: true }
})

const globalStore = useGlobalStore()

const { customHeader, openDialogField } = toRefs(props)

const closeDialog = () => {
  globalStore[openDialogField.value] = false

  emit('closeDialog')
}

const submitDialog = () => {
  emit('submitDialog')
};
</script>
<template>
<section>
  <TransitionRoot as="template" :show="globalStore[openDialogField]">
    <Dialog as="div" class="relative z-[1999]" @close="closeDialog">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-[0.38] transition-opacity backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex max-md:h-screen md:min-h-full overflow-y-auto w-full justify-center md:items-center md:p-[30px]">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
            enter-to="opacity-100 translate-y-0 md:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 md:scale-100"
            leave-to="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
          >
            <DialogPanel class="relative md:rounded-lg bg-white md:p-5 md:pt-0 shadow-xl transition-all w-full md:max-w-xl flex-1 max-md:overflow-hidden flex flex-col text-sm">
              <section class="flex flex-col flex-1 min-h-0 child:max-md:px-5 max-md:overflow-hidden">
                <header class="flex items-center justify-between border-b py-4">
                  <slot name="customHeader" v-if="customHeader"></slot>

                  <DialogTitle class="text-lg font-semibold" v-else>
                    <slot name="defaultHeader"></slot>
                  </DialogTitle>
                  <button @click="closeDialog">
                    <Icon icon="heroicons:x-mark" class="text-xl" />
                  </button>
                </header>
                <slot name="warningBlock" v-if="warningBlock"></slot>
                <section
                  class="pt-3 flex-1 min-h-0 overflow-y-auto md:max-h-[calc(100vh-230px)] md:px-5 md:-mx-5"
                >
                  <slot name="dialogBody"></slot>
                </section>
                <footer class="flex items-stretch gap-x-4 child:max-md:flex-1 border-t pt-2 max-md:pb-4" :class="{ '!w-full !justify-center': !cancelButtonNeeded }">
                  <button class="primary-button" :class="{ 'px-20': !cancelButtonNeeded }" @click.prevent="submitDialog">{{ customSubmitButtonText }}</button>
                  <button v-if="cancelButtonNeeded" class="alternative-button" @click.prevent="closeDialog">{{ customCancelButtonText }}</button>
                </footer>
              </section>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</section>
</template>